import { api } from '../../api/client';
import { processImage } from '../../utils/imageProcessing';

// ── URL helpers ──────────────────────────────────────────────────────────────
export function stripSignature(url) {
  if (!url) return url;
  try { return url.split('?')[0]; } catch { return url; }
}

export function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function buildBibleGatewayUrl(ref, code) {
  return `https://www.biblegateway.com/passage/?search=${encodeURIComponent(ref)}&version=${code}`;
}

// Auto-resize textarea to fit content while keeping notepad lines aligned
export function autoResize(el) {
  if (!el) return;
  el.style.height = 'auto';
  const lines = Math.ceil((el.scrollHeight - 24) / 24);
  el.style.height = (12 + lines * 24 + 12) + 'px';
}

// ── Image / video upload helpers ─────────────────────────────────────────────
export async function uploadPlanImage(file) {
  const { blob, type } = await processImage(file, { preset: 'plan' });
  const { uploadUrl, fileUrl } = await api.post('/api/upload/presign', {
    fileType: type,
    fileName: (file.name || 'plan-cover').replace(/\.\w+$/, type === 'image/webp' ? '.webp' : '.jpg'),
    fileSize: blob.size,
  });
  await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': type } });
  const previewUrl = URL.createObjectURL(blob);
  return { fileUrl, previewUrl };
}

export async function uploadPlanVideo(file) {
  const { uploadUrl, fileUrl } = await api.post('/api/upload/presign', {
    fileType: file.type, fileName: file.name || 'plan-video', fileSize: file.size,
  });
  await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
  const previewUrl = URL.createObjectURL(file);
  return { fileUrl, previewUrl };
}

// ── Default questions ────────────────────────────────────────────────────────
export const DEFAULT_QUESTIONS = [
  'Being with Jesus: What do I notice about Jesus? What does this passage reveal about his heart, his feelings for the Father and people, and his priorities?',
  'Listening to His Voice: What message does Jesus seem to be bringing here? What part of that message speaks most deeply to me today?',
  'Watching His Way: How does Jesus relate to people — the forgotten, the proud, the needy, the faithful? What do I learn from how he treats or responds to others?',
  'Letting His Life Shape Mine: What aspect of Jesus\' teaching or way of living can I imitate in my own thoughts or actions? What specific step can I take today?',
  'Growing in Love: What draws my affection or admiration for Jesus as I reflect on this passage? How is my understanding of him deepening, and how is that stirring greater love, trust, or surrender?',
];

// ── API helpers ──────────────────────────────────────────────────────────────
export const growthApi = {
  list:         (opts = {})  => {
    const qs = opts.language ? `?language=${encodeURIComponent(opts.language)}` : '';
    return api.get(`/api/growth${qs}`);
  },
  get:          (id)         => api.get(`/api/growth/${id}`),
  enroll:       (id, opts)   => api.post(`/api/growth/${id}/enroll`, opts),
  unenroll:     (id)         => api.delete(`/api/growth/${id}/enroll`),
  getUnit:      (pid, uid)   => api.get(`/api/growth/${pid}/units/${uid}`),
  saveResponse: (pid, uid, d)=> api.put(`/api/growth/${pid}/units/${uid}/response`, d),
  // admin
  create:       (d)          => api.post('/api/growth', d),
  update:       (id, d)      => api.put(`/api/growth/${id}`, d),
  generate:         (id)         => api.post(`/api/growth/${id}/generate`),
  generateOutline:  (id)         => api.post(`/api/growth/${id}/generate/outline`),
  generateCommentary:(id, unitIds) => api.post(`/api/growth/${id}/generate/commentary`, { unitIds }),
  createUnit:   (id, d)      => api.post(`/api/growth/${id}/units`, d),
  updateUnit:   (pid, uid, d)=> api.put(`/api/growth/${pid}/units/${uid}`, d),
  deleteUnit:   (pid, uid)   => api.delete(`/api/growth/${pid}/units/${uid}`),
  deletePlan:   (id)         => api.delete(`/api/growth/${id}`),
  regenerateAudio: (pid, uid) => api.post(`/api/growth/${pid}/units/${uid}/audio`),
  generateCoverImage: (id, { force } = {}) => api.post(`/api/growth/${id}/generate/cover-image${force ? '?force=true' : ''}`),
  exportPlan: async (id, title) => {
    const { Auth } = await import('aws-amplify');
    let token = null;
    try { token = (await Auth.currentSession()).getIdToken().getJwtToken(); } catch {}
    const BASE = process.env.REACT_APP_API_URL || '';
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/api/growth/${id}/export`, { headers });
    if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(title || 'growth_plan').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_growth_plan.docx`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
  },
  getInvitation: (pid, uid)   => api.get(`/api/growth/${pid}/units/${uid}/invitation`),
  getBenediction:(pid, uid, d)=> api.post(`/api/growth/${pid}/units/${uid}/benediction`, d),
  getJournal:    (id)         => api.get(`/api/growth/${id}/journal`),
  complete:      (id)         => api.post(`/api/growth/${id}/complete`),
  getActivity: (id, { groupId, circleId } = {}) => {
    const params = new URLSearchParams();
    if (groupId) params.set('groupId', groupId);
    if (circleId) params.set('circleId', circleId);
    const qs = params.toString();
    return api.get(`/api/growth/${id}/activity${qs ? `?${qs}` : ''}`);
  },
  getParticipants: (id, { groupId, circleId } = {}) => {
    const params = new URLSearchParams();
    if (groupId) params.set('groupId', groupId);
    if (circleId) params.set('circleId', circleId);
    const qs = params.toString();
    return api.get(`/api/growth/${id}/participants${qs ? `?${qs}` : ''}`);
  },
  // Circle invites
  circleInvite:        (planId, circleId, userId) => api.post(`/api/growth/${planId}/circles/${circleId}/invites`, { userId }),
  circleInviteRespond: (planId, circleId, inviteId, action) => api.put(`/api/growth/${planId}/circles/${circleId}/invites/${inviteId}`, { action }),
  circleInvites:       (planId, circleId) => api.get(`/api/growth/${planId}/circles/${circleId}/invites`),
  getCommunityPlans:(groupId)  => api.get(`/api/growth/community/${groupId}`),
  userCreate:       (d)        => api.post('/api/growth/user-create', d),
  switchLanguage:   (id)       => api.post(`/api/growth/${id}/switch-language`),
  translateSharedPost: (postId) => api.post(`/api/growth/posts/${postId}/translate`),
};

// ── Two-phase AI generation orchestrator ─────────────────────────────────────
// Phase 1: outline (all units, fast)
// Phase 2: commentary in batches of 4, max 2 concurrent, with 1 retry each

async function withRetry(fn, retries = 1, delayMs = 3000) {
  try { return await fn(); }
  catch (err) {
    if (retries <= 0) throw err;
    await new Promise(r => setTimeout(r, delayMs));
    return withRetry(fn, retries - 1, delayMs);
  }
}

async function runWithConcurrency(tasks, limit) {
  const results = [];
  const executing = new Set();
  for (const task of tasks) {
    const p = task().then(r => { executing.delete(p); return r; });
    executing.add(p);
    results.push(p);
    if (executing.size >= limit) await Promise.race(executing);
  }
  return Promise.allSettled(results);
}

export async function runTwoPhaseGeneration(planId, totalUnits, onStatus) {
  const BATCH_SIZE = 4;

  // Phase 1 — outline (also returns AI-suggested description, color, contentRating)
  onStatus('Building outline…');
  const outlineResult = await growthApi.generateOutline(planId);
  const units = outlineResult.units || [];
  if (units.length === 0) throw new Error('AI returned empty outline. Please try again.');

  // Phase 1.5 + Phase 2 — cover image + commentary
  const batches = [];
  for (let i = 0; i < units.length; i += BATCH_SIZE) {
    batches.push(units.slice(i, i + BATCH_SIZE).map(u => u.id));
  }

  const batchCount = batches.length;
  let doneCount = 0;
  const failedBatches = [];

  onStatus(`Outline ready — creating cover photo & writing commentary (0/${batchCount} batches)…`);

  // Cover image fires independently (non-blocking)
  const coverPromise = growthApi.generateCoverImage(planId)
    .catch(() => onStatus('Cover image failed — you can add one manually.'));

  // Commentary batches: max 2 concurrent, each with 1 retry
  const batchTasks = batches.map((unitIds, idx) => () =>
    withRetry(() => growthApi.generateCommentary(planId, unitIds))
      .then(() => {
        doneCount++;
        onStatus(`Creating cover photo & writing commentary… ${doneCount}/${batchCount} batches done`);
      })
      .catch(() => { failedBatches.push(idx + 1); })
  );

  await Promise.all([coverPromise, runWithConcurrency(batchTasks, 2)]);

  if (failedBatches.length > 0) {
    onStatus(`Done — but batch(es) ${failedBatches.join(', ')} failed. You can edit those days manually or retry.`);
  } else {
    onStatus('Done!');
  }

  // Return meta so CreatePlanModal can update its display
  return outlineResult.meta || {};
}
